import { useRef, useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import authService from "../../services/auth";
import "./Settings.css";
import settingAvatar from "../../assets/images/setting-image.png";

export default function Settings() {
  const fileRef = useRef(null);
  const { user, updateUser } = useAuth();

  // User profile state
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  // Company information state
  const [companyForm, setCompanyForm] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zipCode: "",
  });
  const [companyLoading, setCompanyLoading] = useState(false);

  // Settings state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [dndEnabled, setDndEnabled] = useState(false);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load user data on mount
  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
      });

      if (user.company) {
        setCompanyForm({
          name: user.company.name || "",
          street: user.company.address?.street || "",
          city: user.company.address?.city || "",
          state: user.company.address?.state || "",
          zipCode: user.company.address?.zipCode || "",
        });
      }
    }
  }, [user]);

  // Apply theme on change
  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const onPickFile = () => fileRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError("File size must be less than 5MB");
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError("Only image files (JPEG, PNG, GIF, WebP) are allowed");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await authService.uploadAvatar(formData);

      // Update user context with full user object from response
      if (response.user) {
        updateUser(response.user);
      }
      setSuccess("Avatar uploaded successfully!");
    } catch (err) {
      setError(err.message || "Failed to upload avatar");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const updatedProfile = await authService.updateProfile(form);
      updateUser({ ...user, ...updatedProfile });
      setSuccess("Profile updated successfully!");
    } catch (err) {
      setError(err.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);

    try {
      await authService.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );
      setSuccess("Password changed successfully!");
      setShowPasswordModal(false);
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      setError(err.message || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyUpdate = async (e) => {
    e.preventDefault();
    setCompanyLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const companyData = {
        name: companyForm.name,
        address: {
          street: companyForm.street,
          city: companyForm.city,
          state: companyForm.state,
          zipCode: companyForm.zipCode,
        },
      };

      const updatedCompany = await authService.updateCompany(companyData);

      // Update user context with new company data
      updateUser({
        ...user,
        company: {
          ...user.company,
          ...updatedCompany,
        },
      });

      setSuccess("Company information updated successfully!");
    } catch (err) {
      setError(err.message || "Failed to update company information");
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    setCompanyForm(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="settings-container">
      <section className="mq-dashboard-header">
        <div>
          <p className="mq-dashboard-welcome-label">← Back Home</p>
          <h1 className="mq-dashboard-welcome-name">Settings</h1>
        </div>
      </section>

      {/* Success/Error Messages */}
      {error && (
        <div className="settings-message settings-message-error">
          {error}
        </div>
      )}
      {success && (
        <div className="settings-message settings-message-success">
          {success}
        </div>
      )}

      {/* Personal Info */}
      <section className="settings-card">
        <div className="settings-card-head">Personal Information</div>

        <form className="settings-content" onSubmit={handleProfileUpdate}>
          <div className="settings-form">
            <div className="row-2">
              <div className="field">
                <label>First Name</label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, firstName: e.target.value }))
                  }
                  className="input"
                  placeholder="John"
                  required
                />
              </div>
              <div className="field">
                <label>Last Name</label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, lastName: e.target.value }))
                  }
                  className="input"
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            <div className="field">
              <label>Email Address</label>
              <input
                type="email"
                value={form.email}
                disabled
                className="input"
              />
              <small className="field-hint">
                Email cannot be changed
              </small>
            </div>

            <div className="field">
              <label>Phone Number</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) =>
                  setForm((p) => ({ ...p, phone: e.target.value }))
                }
                className="input"
                placeholder="+1 (555) 000-0000"
              />
            </div>

            <div className="field password-field">
              <label>Password</label>
              <div className="password-input-wrapper">
                <input
                  value="••••••••••••"
                  readOnly
                  className="input"
                />
                <button
                  type="button"
                  className="btn-change"
                  onClick={() => setShowPasswordModal(true)}
                >
                  Change
                </button>
              </div>
            </div>

            <div className="field">
              <button
                type="submit"
                className="btn-save"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

          {/* Avatar */}
          <div className="settings-avatar-section">
            <div className="avatar">
              <img src={user?.avatar || settingAvatar} alt="User avatar" />
            </div>
            <button
              type="button"
              className="btn-change-avatar"
              onClick={onPickFile}
              disabled={loading}
            >
              {loading ? "Uploading..." : "Change"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              hidden
            />
          </div>
        </form>
      </section>

      {/* Company Information */}
      {user?.company && (
        <section className="settings-card">
          <div className="settings-card-head">Company Information</div>
          <form className="settings-content" onSubmit={handleCompanyUpdate}>
            <div className="settings-form">
              <div className="field">
                <label>Company Name</label>
                <input
                  type="text"
                  name="name"
                  value={companyForm.name}
                  onChange={handleCompanyChange}
                  className="input"
                  required
                />
              </div>
              <div className="field">
                <label>Street Address</label>
                <input
                  type="text"
                  name="street"
                  value={companyForm.street}
                  onChange={handleCompanyChange}
                  className="input"
                  placeholder="123 Main Street"
                />
              </div>
              <div className="field-row">
                <div className="field">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={companyForm.city}
                    onChange={handleCompanyChange}
                    className="input"
                    placeholder="City"
                  />
                </div>
                <div className="field">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={companyForm.state}
                    onChange={handleCompanyChange}
                    className="input"
                    placeholder="State"
                  />
                </div>
                <div className="field">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={companyForm.zipCode}
                    onChange={handleCompanyChange}
                    className="input"
                    placeholder="ZIP"
                  />
                </div>
              </div>
            </div>
            <div className="settings-actions">
              <button
                type="submit"
                className="btn-save"
                disabled={companyLoading}
              >
                {companyLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* System Settings */}
      <section className="settings-card">
        <div className="settings-card-head">System Settings</div>

        <div className="settings-row between">
          <div className="setting-block">
            <div className="setting-title">Notification Settings</div>
          </div>
          <div className="setting-controls">
            <label className="check">
              <input
                type="checkbox"
                checked={pushEnabled}
                onChange={(e) => setPushEnabled(e.target.checked)}
              />
              <span>Push notifications</span>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={dndEnabled}
                onChange={(e) => setDndEnabled(e.target.checked)}
              />
              <span>Do not disturb schedule</span>
            </label>
          </div>
        </div>

        <div className="divider" />

        <div className="settings-row between">
          <div className="setting-block">
            <div className="setting-title">Display Preferences</div>
          </div>
          <div className="setting-controls">
            <label className="radio">
              <input
                type="radio"
                name="theme"
                value="dark"
                checked={theme === "dark"}
                onChange={(e) => setTheme(e.target.value)}
              />
              <span>Dark mode</span>
            </label>
            <label className="radio">
              <input
                type="radio"
                name="theme"
                value="light"
                checked={theme === "light"}
                onChange={(e) => setTheme(e.target.value)}
              />
              <span>Light mode</span>
            </label>
          </div>
        </div>
      </section>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button
                className="modal-close"
                onClick={() => setShowPasswordModal(false)}
              >
                ×
              </button>
            </div>

            {error && (
              <div className="settings-message settings-message-error">
                {error}
              </div>
            )}

            <form onSubmit={handlePasswordChange}>
              <div className="field">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm((p) => ({
                      ...p,
                      currentPassword: e.target.value,
                    }))
                  }
                  className="input"
                  required
                />
              </div>

              <div className="field">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm((p) => ({
                      ...p,
                      newPassword: e.target.value,
                    }))
                  }
                  className="input"
                  required
                  minLength={8}
                />
                <small className="field-hint">
                  Password must be at least 8 characters
                </small>
              </div>

              <div className="field">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm((p) => ({
                      ...p,
                      confirmPassword: e.target.value,
                    }))
                  }
                  className="input"
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={loading}
                >
                  {loading ? "Changing..." : "Change Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
